'use client';

import type { VariantProps } from 'class-variance-authority';
import type { ReactNode, RefCallback } from 'react';
import { cva } from 'class-variance-authority';
import { GmTabsSkeleton } from '@/components/entities/gamification/skeletons/GmTabsSkeleton';
import { NotificationBadge } from '@/components/shared/NotificationBadge';
import { useTabIndicator } from '@/hooks/useTabIndicator';
import { cn } from '@/utils/cn';

interface GamificationTabsListItem<Value extends string> {
  value: Value;
  title: ReactNode;
  badge?: ReactNode;
}

interface RenderTabArgs<Value extends string, Element extends HTMLElement> {
  tab: GamificationTabsListItem<Value>;
  index: number;
  isActive: boolean;
  className: string;
  ref: RefCallback<Element>;
  children: ReactNode;
}

interface GamificationTabsListProps<Value extends string, Element extends HTMLElement> {
  tabs: readonly GamificationTabsListItem<Value>[];
  activeValue: Value;
  renderTab: (args: RenderTabArgs<Value, Element>) => ReactNode;
  ariaLabel?: string;
  role?: 'tablist';
  className?: string;
  listClassName?: string;
  itemClassName?: string;
  loading?: boolean;
  titleClassName?: string;
}

const gmTabsListVariants = cva(
  'relative overflow-hidden',
  {
    variants: {
      variant: {
        mobile: 'mb-3 grid grid-cols-auto rounded-2xl border border-main-800',
        desktop: 'w-max max-w-full',
        responsive: 'max-md:w-full w-max max-w-full max-lg:rounded-2xl max-lg:border max-lg:border-main-800',
      },
    },
    defaultVariants: {
      variant: 'responsive',
    },
  },
);

const gmTabVariants = cva(
  'relative z-10 flex min-w-0 items-center justify-center gap-2 rounded-[0.75rem] px-4 text-sm font-medium leading-5 transition-colors duration-300',
  {
    variants: {
      variant: {
        mobile: 'py-3 w-full',
        desktop: 'py-2.5 min-w-25',
        responsive: 'min-w-25 py-3 lg:py-2.5 max-lg:w-full',
      },
    },
    defaultVariants: {
      variant: 'responsive',
    },
  },
);

export const GamificationTabsList = <Value extends string, Element extends HTMLElement>({
  tabs,
  activeValue,
  renderTab,
  ariaLabel,
  role = 'tablist',
  className,
  variant,
  loading,
  listClassName,
  itemClassName,
  titleClassName,
}: GamificationTabsListProps<Value, Element> & VariantProps<typeof gmTabsListVariants>) => {
  const activeIndex = Math.max(0, tabs.findIndex(tab => tab.value === activeValue));
  const { tabRef, indicatorStyle } = useTabIndicator<Element>(activeIndex, { deps: [activeValue, tabs] });

  if (loading) {
    return <GmTabsSkeleton />;
  }

  return (
    <nav className={cn('relative overflow-hidden', gmTabsListVariants({ className, variant }))} aria-label={ariaLabel}>
      <ul
        className={
          cn(
            'relative flex overflow-x-auto hide-scrollbar p-1 grid-flow-col md:px-0 gap-x-1 auto-cols-fr',
            variant === 'mobile' ? 'grid' : 'max-md:grid',
            listClassName,
          )
        }
        role={role}
      >
        <div
          className={cn(
            'pointer-events-none absolute top-1 h-[calc(100%-0.5rem)] rounded-[0.75rem] bg-main-back-gradient p-px transition-all duration-500',
            indicatorStyle.width ? 'opacity-100' : 'opacity-0',
          )}
          style={{ left: indicatorStyle.left, width: indicatorStyle.width }}
        >
          <div className="size-full rounded-[0.75rem] bg-main-800" />
        </div>

        {tabs.map((tab, index) => {
          const isActive = activeValue === tab.value;

          return (
            <li key={tab.value} className="min-w-0">
              {renderTab({
                tab,
                index,
                isActive,
                ref: (el) => {
                  tabRef.current[index] = el;
                },
                className: cn(
                  gmTabVariants({ variant }),
                  isActive ? 'text-white' : 'text-c300 lg:hover:text-light lg:hover:bg-main-800',
                  itemClassName,
                ),
                children: (
                  <>
                    <span className={titleClassName}>{tab.title}</span>
                    {tab.badge && (
                      <NotificationBadge>
                        {tab.badge}
                      </NotificationBadge>
                    )}
                  </>
                ),
              })}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};
